import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ShoppingBag,
  Trash2,
  Lock,
  ArrowLeft,
  Tag,
  CheckCircle2,
  Truck,
  ShieldCheck,
  RotateCcw,
  Check
} from 'lucide-react';
import { reserveCartStockBackend, BACKEND_URL, getProductImageUrl } from '../services/api';

const Cart = ({ cartItems = [], onUpdateQuantity, onRemoveItem }) => {
  const navigate = useNavigate();
  const [couponCode, setCouponCode] = useState('');
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [couponMsg, setCouponMsg] = useState({ type: '', text: '' });
  const [couponLoading, setCouponLoading] = useState(false);

  // Load Pre-Applied Coupon from Session Storage if available
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem('appliedCoupon');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && parsed.code) {
          setCouponCode(parsed.code);
          setCouponDiscount(Number(parsed.discountPct || 10));
          setCouponMsg({ type: 'success', text: `Coupon "${parsed.code}" active (${parsed.discountPct}% OFF)` });
        }
      }
    } catch (e) {}
  }, []);

  // Live Shipping & Tax Settings State
  const [shippingConfig, setShippingConfig] = useState({
    standard_shipping_fee: 15.00,
    free_shipping_threshold: 500.00,
    vat_tax_percent: 20.00
  });

  // Fetch Shipping & Tax Configuration from Backend API
  useEffect(() => {
    let isMounted = true;
    const fetchShippingSettings = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/settings`);
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.settings && isMounted) {
            setShippingConfig({
              standard_shipping_fee: Number(data.settings.standard_shipping_fee || data.settings.shipping_rate || 15.00),
              free_shipping_threshold: Number(data.settings.free_shipping_threshold || 500.00),
              vat_tax_percent: Number(data.settings.vat_tax_percent !== undefined && data.settings.vat_tax_percent !== null ? data.settings.vat_tax_percent : 20.00)
            });
          }
        }
      } catch (err) {
        console.warn("Could not load live shipping and tax settings:", err);
      }
    };
    fetchShippingSettings();
    return () => { isMounted = false; };
  }, []);

  // Calculate Subtotal for valid non-out-of-stock items
  const validItems = cartItems.filter(item => !item.is_out_of_stock && (item.stock === undefined || item.stock > 0));
  const totalCount = cartItems.reduce((acc, item) => acc + (item.quantity || 1), 0);

  const rawSubtotal = validItems.reduce((acc, item) => {
    const p = Number(item.price || item.sale_price || 0);
    return acc + p * (item.quantity || 1);
  }, 0);

  const discountAmount = (rawSubtotal * couponDiscount) / 100;
  const subtotalAfterDiscount = Math.max(0, rawSubtotal - discountAmount);

  // Dynamic Shipping & Free Shipping Calculation
  const isFreeShipping = subtotalAfterDiscount >= shippingConfig.free_shipping_threshold;
  const shippingFee = isFreeShipping ? 0 : shippingConfig.standard_shipping_fee;
  const amountNeededForFreeShipping = Math.max(0, shippingConfig.free_shipping_threshold - subtotalAfterDiscount);

  // Dynamic VAT Tax Calculation based on Admin Settings
  const taxAmount = subtotalAfterDiscount * (shippingConfig.vat_tax_percent / 100);
  const grandTotal = subtotalAfterDiscount + shippingFee + taxAmount;

  const hasOutOfStockItems = cartItems.some(item => item.is_out_of_stock || item.stock === 0);

  const handleApplyCoupon = async (e) => {
    e.preventDefault();
    if (!couponCode.trim()) return;
    setCouponLoading(true);
    setCouponMsg({ type: '', text: '' });

    try {
      const token = localStorage.getItem('userToken') || localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/coupons/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ code: couponCode.trim(), cartTotal: rawSubtotal })
      });
      const data = await response.json();

      if (data.success && data.coupon) {
        const discountPct = Number(data.coupon.discount_percent || 0);
        const discountAmt = Number(data.coupon.discount_amount || 0);
        const codeUpper = couponCode.trim().toUpperCase();
        setCouponDiscount(discountPct);
        sessionStorage.setItem('appliedCoupon', JSON.stringify({
          code: codeUpper,
          discountPct,
          discountAmount: discountAmt,
          discountType: data.coupon.discount_type || 'percentage'
        }));
        setCouponMsg({ type: 'success', text: `Coupon "${codeUpper}" applied! Discount applied.` });
      } else {
        setCouponDiscount(0);
        sessionStorage.removeItem('appliedCoupon');
        const errMsg = data.requires_login
          ? 'Please log in or proceed to checkout with your email to redeem discount coupons.'
          : (data.message || 'Invalid or expired coupon code.');
        setCouponMsg({ type: 'error', text: errMsg });
      }
    } catch (err) {
      setCouponDiscount(0);
      setCouponMsg({ type: 'error', text: 'Could not validate coupon code.' });
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setCouponDiscount(0);
    setCouponCode('');
    sessionStorage.removeItem('appliedCoupon');
    setCouponMsg({ type: '', text: '' });
  };

  const handleProceedToCheckout = async () => {
    if (hasOutOfStockItems) return;
    try {
      await reserveCartStockBackend();
      navigate('/checkout');
    } catch (err) {
      navigate('/checkout');
    }
  };

  return (
    <div className="cart-page-container" style={{ minHeight: '100vh', background: '#fafbfc', padding: '40px 24px', fontFamily: "'Plus Jakarta Sans', sans-serif", width: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}>
      <div style={{ maxWidth: '1280px', margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>
        
        {/* 1. BREADCRUMBS & HEADER ROW */}
        <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>
          <Link to="/" style={{ color: '#64748b', textDecoration: 'none' }}>Home</Link>
          <span style={{ margin: '0 8px' }}>&gt;</span>
          <span style={{ color: '#0f172a', fontWeight: 600 }}>Cart</span>
        </div>

        <div className="cart-header-row" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '32px', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h1 style={{ fontSize: '36px', fontWeight: 900, color: '#0f172a', letterSpacing: '-0.02em', margin: '0 0 6px 0' }}>
              Your Cart <span style={{ color: '#dc2626' }}>({totalCount} {totalCount === 1 ? 'Item' : 'Items'})</span>
            </h1>
            <p style={{ fontSize: '14.5px', color: '#64748b', margin: 0 }}>
              Review your items and proceed to secure checkout.
            </p>
          </div>

          <Link
            to="/shop"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              color: '#dc2626',
              fontWeight: 800,
              fontSize: '14px',
              textDecoration: 'none',
              marginTop: '8px'
            }}
          >
            <ArrowLeft size={16} />
            <span>Continue Shopping</span>
          </Link>
        </div>

        {cartItems.length === 0 ? (
          /* EMPTY CART STAGE - NO WHITE BG */
          <div style={{ background: 'transparent', padding: '60px 24px', textAlign: 'center' }}>
            <ShoppingBag size={56} color="#dc2626" style={{ margin: '0 auto 16px' }} />
            <h3 style={{ fontSize: '22px', fontWeight: 900, color: '#0f172a', marginBottom: '8px' }}>Your shopping cart is empty</h3>
            <p style={{ fontSize: '14.5px', color: '#64748b', maxWidth: '420px', margin: '0 auto 24px' }}>
              Explore our range of clinically proven red light therapy panels and enhance your recovery today.
            </p>
            <Link to="/shop" style={{ background: '#dc2626', color: '#ffffff', padding: '12px 32px', borderRadius: '9999px', fontSize: '14.5px', fontWeight: 800, textDecoration: 'none', display: 'inline-block' }}>
              Explore Shop Products
            </Link>
          </div>
        ) : (
          
          /* MAIN CART RESPONSIVE GRID */
          <div className="cart-page-grid">
            
            {/* LEFT COLUMN: CART ITEMS TABLE + DISCOUNT BAR */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', minWidth: 0 }}>
              
              {/* TABLE CONTAINER CARD */}
              <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '20px', padding: '24px', boxShadow: '0 4px 15px rgba(0,0,0,0.02)' }}>
                
                {/* TABLE SCROLL WRAPPER PREVENTS SCREEN OVERFLOW */}
                <div className="cart-table-wrapper">
                  <div className="cart-table-content" style={{ minWidth: '540px' }}>
                    
                    {/* COMPACT CLEAN TABLE HEADERS */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 90px 120px 90px 40px', gap: '16px', borderBottom: '1px solid #f1f5f9', paddingBottom: '14px', fontSize: '11px', fontWeight: 900, color: '#64748b', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                      <div>PRODUCT</div>
                      <div style={{ textAlign: 'right' }}>PRICE</div>
                      <div style={{ textAlign: 'center' }}>QUANTITY</div>
                      <div style={{ textAlign: 'right' }}>TOTAL</div>
                      <div></div>
                    </div>

                    {/* COMPACT TABLE ROWS (SMALL IMAGE & NAME ONLY) */}
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      {cartItems.map((item) => {
                        const isOut = item.is_out_of_stock || item.stock === 0;
                        const availableStock = item.available_stock || item.stock || 99;
                        const unitPrice = Number(item.price || item.sale_price || 0);
                        const qty = item.quantity || 1;
                        const rowTotal = unitPrice * qty;
                        const stockTag = item.stock_tag || (isOut ? 'Out of Stock' : null);

                        return (
                          <div
                            key={item.cart_item_id || item.id}
                            style={{
                              display: 'grid',
                              gridTemplateColumns: '1fr 90px 120px 90px 40px',
                              gap: '16px',
                              alignItems: 'center',
                              padding: '16px 0',
                              borderBottom: '1px solid #f1f5f9',
                              opacity: isOut ? 0.75 : 1
                            }}
                          >
                            {/* 1. COMPACT PRODUCT COLUMN (SMALL IMAGE + NAME ONLY) */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                              <Link
                                to={`/product/${item.id}`}
                                style={{
                                  width: '64px',
                                  height: '64px',
                                  background: 'transparent',
                                  border: 'none',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  padding: 0,
                                  flexShrink: 0,
                                  textDecoration: 'none'
                                }}
                              >
                                <img
                                  src={getProductImageUrl(item.image_url || item.image)}
                                  alt={item.name}
                                  style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}
                                  onError={(e) => { e.target.src = getProductImageUrl('/uploads/panel_72led.jpg'); }}
                                />
                              </Link>

                              <div>
                                <Link to={`/product/${item.id}`} style={{ textDecoration: 'none' }}>
                                  <h3 style={{ fontSize: '15.5px', fontWeight: 800, color: '#0f172a', margin: '0 0 4px 0', lineHeight: 1.3 }}>
                                    {item.name}
                                  </h3>
                                </Link>

                                {/* Optional Add-ons List */}
                                {Array.isArray(item.selected_addons) && item.selected_addons.length > 0 && (
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '6px' }}>
                                    {item.selected_addons.map((addon, aIdx) => (
                                      <span key={aIdx} style={{ fontSize: '10.5px', fontWeight: 700, color: '#dc2626', background: '#fef2f2', border: '1px solid #fee2e2', padding: '1px 7px', borderRadius: '4px' }}>
                                        + {addon.addon_name || addon.name} (+£{Number(addon.addon_price || addon.price || 0).toFixed(2)})
                                      </span>
                                    ))}
                                  </div>
                                )}

                                {/* Compact Stock Badge */}
                                {isOut ? (
                                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '11px', fontWeight: 800, color: '#dc2626', background: '#fef2f2', border: '1px solid #fecaca', padding: '2px 8px', borderRadius: '9999px' }}>
                                    <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#dc2626' }}></span>
                                    <span>Out of Stock</span>
                                  </div>
                                ) : stockTag ? (
                                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '11px', fontWeight: 800, color: '#d97706', background: '#fffbeb', border: '1px solid #fef3c7', padding: '2px 8px', borderRadius: '9999px' }}>
                                    <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#d97706' }}></span>
                                    <span>{stockTag}</span>
                                  </div>
                                ) : (
                                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '11px', fontWeight: 800, color: '#16a34a', background: '#dcfce7', padding: '2px 8px', borderRadius: '9999px' }}>
                                    <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#16a34a' }}></span>
                                    <span>In Stock</span>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* 2. Unit Price */}
                            <div style={{ textAlign: 'right', fontSize: '15px', fontWeight: 800, color: '#0f172a' }}>
                              £{unitPrice.toFixed(2)}
                            </div>

                            {/* 3. Quantity Pill (- 1 +) */}
                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                              {!isOut && onUpdateQuantity ? (
                                <div style={{ display: 'inline-flex', alignItems: 'center', background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '3px 8px', gap: '10px' }}>
                                  <button
                                    onClick={() => onUpdateQuantity(item.id || item.product_id, -1, availableStock)}
                                    style={{ background: 'none', border: 'none', color: '#0f172a', fontWeight: 900, fontSize: '15px', cursor: 'pointer', padding: 0 }}
                                  >
                                    -
                                  </button>
                                  <span style={{ fontSize: '13px', fontWeight: 900, color: '#0f172a', minWidth: '14px', textAlign: 'center' }}>
                                    {qty}
                                  </span>
                                  <button
                                    onClick={() => onUpdateQuantity(item.id || item.product_id, 1, availableStock)}
                                    disabled={qty >= availableStock}
                                    style={{ background: 'none', border: 'none', color: qty >= availableStock ? '#94a3b8' : '#0f172a', fontWeight: 900, fontSize: '15px', cursor: qty >= availableStock ? 'not-allowed' : 'pointer', padding: 0 }}
                                  >
                                    +
                                  </button>
                                </div>
                              ) : (
                                <span style={{ fontSize: '13px', fontWeight: 800, color: '#64748b' }}>Qty: {qty}</span>
                              )}
                            </div>

                            {/* 4. Row Total */}
                            <div style={{ textAlign: 'right', fontSize: '15px', fontWeight: 900, color: '#0f172a' }}>
                              £{rowTotal.toFixed(2)}
                            </div>

                            {/* 5. Trash Remove Button */}
                            <div style={{ textAlign: 'right' }}>
                              <button
                                onClick={() => onRemoveItem && onRemoveItem(item.cart_item_id || item.id)}
                                style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', cursor: 'pointer' }}
                                title="Remove item"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>

                          </div>
                        );
                      })}
                    </div>

                  </div>
                </div>

              </div>

              {/* DISCOUNT CODE BAR */}
              <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '18px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '20px', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', fontWeight: 800, color: '#0f172a' }}>
                  <ShoppingBag size={18} color="#dc2626" />
                  <span>Have a discount code?</span>
                </div>

                <form onSubmit={handleApplyCoupon} style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, maxWidth: '380px' }}>
                  <input
                    type="text"
                    placeholder="Enter coupon code"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value)}
                    style={{ flex: 1, padding: '10px 16px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '13.5px', outline: 'none' }}
                  />
                  <button
                    type="submit"
                    disabled={couponLoading}
                    style={{
                      background: '#ffffff',
                      border: '1px solid #fca5a5',
                      color: '#dc2626',
                      padding: '10px 22px',
                      borderRadius: '10px',
                      fontSize: '13.5px',
                      fontWeight: 800,
                      cursor: couponLoading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {couponLoading ? 'Checking...' : 'Apply'}
                  </button>
                </form>
              </div>

              {couponMsg.text && (
                <div style={{ fontSize: '13px', fontWeight: 700, color: couponMsg.type === 'success' ? '#16a34a' : '#dc2626', padding: '0 8px' }}>
                  {couponMsg.text}
                </div>
              )}

            </div>

            {/* RIGHT COLUMN: ORDER SUMMARY CARD & WE ACCEPT GUARANTEES */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', minWidth: 0 }}>
              
              {/* ORDER SUMMARY CARD */}
              <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '24px', padding: '28px', boxShadow: '0 4px 15px rgba(0,0,0,0.02)' }}>
                <h3 style={{ fontSize: '20px', fontWeight: 900, color: '#0f172a', marginBottom: '24px' }}>
                  Order Summary
                </h3>

                {/* Subtotal Row */}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '14px', fontSize: '14px', color: '#64748b' }}>
                  <span>Subtotal ({validItems.length} {validItems.length === 1 ? 'item' : 'items'})</span>
                  <span style={{ fontWeight: 800, color: '#0f172a' }}>£{rawSubtotal.toFixed(2)}</span>
                </div>

                {couponDiscount > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '14px', fontSize: '14px', color: '#16a34a', fontWeight: 700 }}>
                    <span>Discount ({couponDiscount}%)</span>
                    <span>-£{discountAmount.toFixed(2)}</span>
                  </div>
                )}

                {/* Dynamic Shipping Row */}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '14px', fontSize: '14px', color: '#64748b' }}>
                  <span>Shipping</span>
                  <span style={{ fontWeight: 800, color: isFreeShipping ? '#16a34a' : '#0f172a' }}>
                    {isFreeShipping ? 'Free' : `£${shippingFee.toFixed(2)}`}
                  </span>
                </div>

                {/* Dynamic VAT Tax Row */}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px', fontSize: '14px', color: '#64748b' }}>
                  <span>Tax ({shippingConfig.vat_tax_percent}%)</span>
                  <span style={{ fontWeight: 800, color: '#0f172a' }}>£{taxAmount.toFixed(2)}</span>
                </div>

                {/* Total Line Divider */}
                <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '20px', marginBottom: '24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '18px', fontWeight: 900, color: '#0f172a' }}>Total</span>
                  <span style={{ fontSize: '28px', fontWeight: 900, color: '#dc2626' }}>
                    £{grandTotal.toFixed(2)}
                  </span>
                </div>

                {/* DYNAMIC FREE SHIPPING THRESHOLD ALERT BOX */}
                <div style={{ background: isFreeShipping ? '#f0fdf4' : '#fef2f2', border: `1px solid ${isFreeShipping ? '#bbf7d0' : '#fee2e2'}`, borderRadius: '14px', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                  <Truck size={22} color={isFreeShipping ? "#16a34a" : "#dc2626"} style={{ flexShrink: 0 }} />
                  <div>
                    {isFreeShipping ? (
                      <>
                        <h4 style={{ fontSize: '13px', fontWeight: 800, color: '#166534', margin: '0 0 2px 0' }}>You're eligible for free shipping!</h4>
                        <p style={{ fontSize: '11.5px', color: '#15803d', margin: 0, lineHeight: 1.3 }}>Your order reached £{shippingConfig.free_shipping_threshold.toFixed(2)} and will be delivered at no extra cost.</p>
                      </>
                    ) : (
                      <>
                        <h4 style={{ fontSize: '13px', fontWeight: 800, color: '#991b1b', margin: '0 0 2px 0' }}>
                          Add £{amountNeededForFreeShipping.toFixed(2)} more for FREE Shipping!
                        </h4>
                        <p style={{ fontSize: '11.5px', color: '#b91c1c', margin: 0, lineHeight: 1.3 }}>
                          Standard UK shipping is £{shippingConfig.standard_shipping_fee.toFixed(2)} for orders under £{shippingConfig.free_shipping_threshold.toFixed(2)}.
                        </p>
                      </>
                    )}
                  </div>
                </div>

                {/* Solid Red Secure Checkout Button */}
                <button
                  onClick={handleProceedToCheckout}
                  disabled={hasOutOfStockItems}
                  style={{
                    width: '100%',
                    background: hasOutOfStockItems ? '#cbd5e1' : '#dc2626',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '12px',
                    padding: '16px',
                    fontSize: '15px',
                    fontWeight: 800,
                    cursor: hasOutOfStockItems ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    boxShadow: hasOutOfStockItems ? 'none' : '0 6px 18px rgba(220, 38, 38, 0.35)',
                    marginBottom: '20px'
                  }}
                >
                  <Lock size={18} />
                  <span>{hasOutOfStockItems ? 'Remove Out of Stock Items' : 'Secure Checkout'}</span>
                </button>

                {/* Payment Logos Badge Row */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '14px', opacity: 0.75 }}>
                  <span style={{ fontSize: '11px', fontWeight: 900, color: '#1e3a8a', fontStyle: 'italic' }}>VISA</span>
                  <span style={{ fontSize: '11px', fontWeight: 900, color: '#ea580c' }}>Mastercard</span>
                  <span style={{ fontSize: '11px', fontWeight: 900, color: '#0284c7' }}>PayPal</span>
                  <span style={{ fontSize: '11px', fontWeight: 900, color: '#0f172a' }}>Pay</span>
                  <span style={{ fontSize: '11px', fontWeight: 900, color: '#475569' }}>G Pay</span>
                </div>

              </div>

              {/* WE ACCEPT / GUARANTEES BOX */}
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '20px', padding: '24px' }}>
                <h4 style={{ fontSize: '13px', fontWeight: 800, color: '#0f172a', marginBottom: '14px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  We Accept & Guarantee
                </h4>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '12.5px', color: '#475569' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <CheckCircle2 size={16} color="#dc2626" />
                    <span>60-day money back guarantee</span>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <ShieldCheck size={16} color="#dc2626" />
                    <span>2-year warranty on all panels</span>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Lock size={16} color="#dc2626" />
                    <span>Safe & secure checkout</span>
                  </div>
                </div>
              </div>

            </div>

          </div>
        )}

      </div>
    </div>
  );
};

export default Cart;
